import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

/* ------------------------------- toasts ------------------------------ */

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const push = useCallback((message, tone = 'info', ttl = 5000) => {
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setToasts((t) => [...t, { id, message, tone }]);
    window.setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), ttl);
  }, []);

  const value = useMemo(() => ({
    success: (m) => push(m, 'ok'),
    error: (m) => push(m, 'error', 8000),
    info: (m) => push(m, 'info'),
  }), [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-stack" role="status" aria-live="polite">
        {toasts.map((t) => <div key={t.id} className={`toast ${t.tone}`}>{t.message}</div>)}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}

/* ------------------------------- pieces ------------------------------ */

export function Card({ title, subtitle, actions, children, bodyClass = '' }) {
  return (
    <div className="card">
      {(title || actions) && (
        <div className="card-head">
          <div>
            {title && <h2>{title}</h2>}
            {subtitle && <div className="page-sub" style={{ marginTop: 2 }}>{subtitle}</div>}
          </div>
          <div className="spacer" />
          {actions}
        </div>
      )}
      <div className={`card-body ${bodyClass}`}>{children}</div>
    </div>
  );
}

export function Tile({ label, value, foot, tone = '' }) {
  return (
    <div className={`tile ${tone}`}>
      <div className="tile-label">{label}</div>
      <div className="tile-value">{value}</div>
      {foot && <div className="tile-foot">{foot}</div>}
    </div>
  );
}

export function Badge({ kind, children }) {
  return <span className={`badge badge-${kind}`}>{children}</span>;
}

export function ConfidenceMeter({ value, band }) {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div className="meter-row">
      <div className={`meter ${band || ''}`} role="img" aria-label={`Confidence ${pct.toFixed(1)} percent`}>
        <span style={{ width: `${pct}%` }} />
      </div>
      <span className="meter-val">{pct.toFixed(1)}%</span>
    </div>
  );
}

export function Notice({ tone = 'info', title, children }) {
  return (
    <div className={`notice notice-${tone}`}>
      {title && <strong>{title}</strong>}
      {title && children ? <div style={{ marginTop: 4 }}>{children}</div> : children}
    </div>
  );
}

export function ErrorNotice({ error }) {
  if (!error) return null;
  return (
    <Notice tone="error" title={error.message}>
      {Array.isArray(error.details) && error.details.length > 0 && (
        <ul>
          {error.details.map((d, i) => (
            <li key={i}>{d.field ? `${d.field}: ${d.message}` : (d.message || JSON.stringify(d))}</li>
          ))}
        </ul>
      )}
      {error.details && !Array.isArray(error.details) && (
        <div className="mono" style={{ marginTop: 4 }}>{JSON.stringify(error.details)}</div>
      )}
      {error.requestId && <div className="prov" style={{ marginTop: 4 }}>Request {error.requestId}</div>}
    </Notice>
  );
}

export function Empty({ title, children }) {
  return (
    <div className="empty">
      <div className="empty-title">{title}</div>
      {children}
    </div>
  );
}

export function Loading({ rows = 5 }) {
  return (
    <div style={{ display: 'grid', gap: 10, padding: 4 }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="skeleton" style={{ width: `${100 - i * 7}%` }} />
      ))}
    </div>
  );
}

export function Breadcrumbs({ items }) {
  return (
    <nav className="crumbs" aria-label="Breadcrumb">
      {items.map((item, i) => (
        <span key={i} style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          {i > 0 && <span className="sep">/</span>}
          {item.to ? <Link to={item.to}>{item.label}</Link> : <span>{item.label}</span>}
        </span>
      ))}
    </nav>
  );
}

export function Pager({ total, limit, offset, onChange }) {
  const page = Math.floor(offset / limit) + 1;
  const pages = Math.max(1, Math.ceil(total / limit));
  return (
    <div className="pager">
      <span>
        {total === 0 ? 'No records' : `${offset + 1}-${Math.min(offset + limit, total)} of ${total.toLocaleString('en-IN')}`}
      </span>
      <div className="spacer" />
      <button className="btn btn-sm" disabled={offset === 0} onClick={() => onChange(0)}>First</button>
      <button className="btn btn-sm" disabled={offset === 0} onClick={() => onChange(Math.max(0, offset - limit))}>Previous</button>
      <span>Page {page} of {pages}</span>
      <button className="btn btn-sm" disabled={page >= pages} onClick={() => onChange(offset + limit)}>Next</button>
      <button className="btn btn-sm" disabled={page >= pages} onClick={() => onChange((pages - 1) * limit)}>Last</button>
    </div>
  );
}

export function Modal({ title, onClose, children, footer, wide = false }) {
  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="card modal"
        style={wide ? { width: 'min(980px, 100%)' } : undefined}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="card-head">
          <h2>{title}</h2>
          <div className="spacer" />
          <button className="btn btn-sm btn-ghost" onClick={onClose} aria-label="Close">Close</button>
        </div>
        <div className="card-body" style={{ overflowY: 'auto' }}>{children}</div>
        {footer && <div className="drawer-foot">{footer}</div>}
      </div>
    </div>
  );
}

export function Drawer({ title, subtitle, onClose, nav, children, footer }) {
  return (
    <div className="drawer-backdrop" onClick={onClose} role="presentation">
      <aside className="drawer" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={title}>
        <div className="drawer-head">
          <div>
            <h2>{title}</h2>
            {subtitle && <div className="prov">{subtitle}</div>}
          </div>
          <div className="spacer" />
          {nav}
          <button className="btn btn-sm btn-ghost" onClick={onClose} aria-label="Close panel">Close</button>
        </div>
        <div className="drawer-body">{children}</div>
        {footer && <div className="drawer-foot">{footer}</div>}
      </aside>
    </div>
  );
}

/** Formats a number for Indian-locale display. */
export const fmt = (n) => (n === null || n === undefined ? '-' : Number(n).toLocaleString('en-IN'));

/** Formats a UTC timestamp from the API in the viewer's local timezone. */
export const fmtDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
};

export const vehicleLabel = (v) => (v ? [v.make, v.model, v.variant].filter(Boolean).join(' ') : '-');
